import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { GOALS } from "components/core/blocks/AdPlan/constants";
import useCategories from "hooks/useCategories";
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa6";
import { LuMoveUpRight } from "react-icons/lu";
import { Link } from "react-router-dom";
import { css, cx } from "../../../../styled-system/css";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { useFetch } from "../../../hooks/api";
import { useAppSelector } from "../../../redux/hooks";
import api from "../../../utils/api";
import Accordion from "../../core/basic/Accordion";
import { Button, buttonRecipe } from "../../core/basic/Button";
import { ButtonGroup } from "../../core/basic/ButtonGroup";
import Card from "../../core/basic/Card";
import Loading from "../../core/blocks/Loading";
import { Checkbox } from "../../fields/base/Checkbox";
import { RadioGroup } from "../../fields/base/RadioGroup";
import { TextAreaField } from "../../fields/base/TextAreaField";
import { SearchInput } from "../../fields/base/TextField";
import { Table } from "../../table/Table";
import ProductImage from "../category/listings/ProductImage";
import CategoryFilter from "./CategorySelector";
import { ExplainerBox } from "../../core/basic/ExplainerBox";
import { selectionStateToSelectedIds } from "../../table/utils";
import { Modal } from "../../core/basic/RACModal";

// This is the default, empty array passed into react-table when errors occur.
// It must exist outside the React event loop with the version of react-table
// we use, as it is checked by _identity_ rather than value, and can cause an
// infinite loop since "[]" creates a new array each time.
const DEFAULT_ARRAY = [];

const normalizeChannel = (channel) => {
  switch (channel.toLowerCase()) {
    case "amazon":
      return "AMZ";
    default:
      return channel;
  }
};

function MoveToDialog(props) {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleMove = useCallback(() => {
    props.actionMutation.mutate({
      name: "moveTo",
      category: { id: selectedCategory },
      selection: props.selection,
      allSelected: props.allSelected,
    });
    props.onClose();
  }, [props, selectedCategory]);

  return (
    <Modal
      isOpen={props.isOpen}
      onOpenChange={(isOpen) => !isOpen && props.onClose()}
      className={css({ width: "40vw" })}
      title="Move to..."
    >
      <VStack alignItems={"stretch"} padding="large" overflow="hidden">
        <CategoryFilter currentCategory={selectedCategory} onCategorySelected={setSelectedCategory} />
        <Button alignSelf="center" isDisabled={!selectedCategory} onPress={handleMove}>
          Move
        </Button>
      </VStack>
    </Modal>
  );
}

const ProductListing = ({ currentCategory }) => {
  const channel = useAppSelector((state) => state.channel);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchOn, setMatchOn] = useState("icontains");
  const [searchFields, setSearchFields] = useState({
    sku: true,
    asin: true,
    product_title: true,
    has_an_ad: "all",
    parent_asin: true,
  });
  // Cursor used for database pagination.
  const [cursor, setCursor] = useState(null);
  const [selection, setSelection] = useState({});
  const selectedIds = selectionStateToSelectedIds(selection);
  const [searchAsins, setSearchAsins] = useState("");
  const deferredSearchAsins = useDeferredValue(searchAsins);
  const asinArray = useMemo(() => deferredSearchAsins?.split(/[,\s\n]+/).filter((n) => n), [deferredSearchAsins]);
  const [allSelected, setAllSelected] = useState(false);
  const [isOpenMoveToDialog, setIsOpenMoveToDialog] = useState(false);
  const topOfListingElement = useRef(null);

  // Track walmart vendor accounts for alternate category links since merch dashboard is unsupported without total sales data
  const orgIsWalmartVendor = channel.currentChannel === "walmart" && user.organization_type === "vendor";

  const { categories, currentCategoryDetails } = useCategories(currentCategory, "merchandising");

  // Pack all of our search filters into a consistent hash, meant to
  // be sent as query arguments to calls to /api/search/products.
  const filterArguments = useMemo(() => {
    const args = {
      query: searchQuery,
      category: currentCategory,
      matchOn: matchOn,
      search_fields: Object.keys(searchFields)
        .filter((k) => searchFields[k])
        .join(","),
      channel: normalizeChannel(channel.currentChannel),
      marketplace: marketPlace.marketPlace,
      has_an_ad: searchFields.has_an_ad === "all" ? null : searchFields.has_an_ad === "true",
      asins: asinArray?.filter((n) => n)?.join(","),
    };

    return args;
  }, [searchQuery, matchOn, channel, marketPlace, searchFields, currentCategory, asinArray]);

  const queryClient = useQueryClient();

  const productSearchQuery = useFetch(
    ["searchProducts", filterArguments, cursor],
    "/api/v2/products/",
    {
      ...filterArguments,
      cursor: cursor,
      expand: "advertisements",
    },
    {
      retry: 0,
      placeholderData: keepPreviousData,
      select: (data) => {
        // For some reason, the API returns duplicate products when searching by "has_ad"
        const ids = new Set(); // temp variable to keep track of accepted ids
        const uniqueProducts = data.data.results.filter(({ id }) => !ids.has(id) && ids.add(id));
        data.data.results = uniqueProducts;
        return data;
      },
    },
  );

  const actionMutation = useMutation({
    mutationFn: async (data) => {
      return api.post("/api/v2/products/", data, filterArguments);
    },
    onSuccess: async () => {
      setCursor(null);
      setSelection({});
      setAllSelected(false);
      setSearchAsins("");
      await queryClient.invalidateQueries({
        queryKey: ["searchProducts"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["searchCategories"],
      });
    },
  });

  // Handle moving to the next page of results.
  const handleNext = useCallback(() => {
    const next = new URL(productSearchQuery.data.data.next);
    if (!next) return;
    if (topOfListingElement.current) {
      topOfListingElement.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    setCursor(next.searchParams.get("cursor"));
  }, [productSearchQuery.data]);

  // Handle moving to the previous page of results.
  const handlePrevious = useCallback(() => {
    const previous = new URL(productSearchQuery.data.data.previous);
    if (!previous) return;
    if (topOfListingElement.current) {
      topOfListingElement.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    setCursor(previous.searchParams.get("cursor"));
  }, [productSearchQuery.data]);

  // If the data changed, erase the current cursor used for pagination.
  useEffect(() => {
    setCursor(null);
  }, [currentCategory, channel, marketPlace]);

  const columns = useMemo(() => {
    const columns = [
      {
        header: "",
        accessorKey: "id",
        enableResizing: false,
        size: 70,
        cell: ({ getValue }) => {
          return <ProductImage id={getValue()} divId={getValue()} width="40px" height="40px" />;
        },
        meta: {
          justify: "center",
        },
      },
      {
        accessorKey: "sku",
      },
      {
        accessorKey: "asin",
        header: function Header() {
          const currentChannel = useAppSelector((state) => state.channel.currentChannel);
          return currentChannel === "amazon" ? "ASIN" : "Item ID";
        },
      },
      {
        accessorKey: "title",
        size: 450,
        cell: ({ getValue, row }) => {
          const managedAds = row.original.advertisements?.filter(
            (ad) =>
              ad.ad_goal !== GOALS.IMPORTED_ADS.value ||
              (ad.ad_goal === GOALS.IMPORTED_ADS.value && ad.non_trellis_enrolled_ad_start_date),
          );
          const unmanagedAds = row.original.advertisements?.filter(
            (ad) => ad.ad_goal === GOALS.IMPORTED_ADS.value && !ad.non_trellis_enrolled_ad_start_date,
          );

          return (
            <VStack gap="xsmall">
              {getValue()}
              <Wrap gap="xsmall" justify="start" width="100%">
                {managedAds?.map((v, i) => {
                  let AdIcon = null;
                  let ad_label = "Ad Active";
                  let ad_color = "primary";

                  if (i === 10) {
                    return (
                      <span className="d-block p-2" key={v.id}>
                        +{managedAds?.length - (i + 1)} more managed media plans
                      </span>
                    );
                  }

                  if (i > 10) {
                    return null;
                  }

                  if ([1, 22, 2].includes(v.ad_status)) {
                    AdIcon = FaPlay;
                    ad_color = "active";
                    ad_label = `Active`;
                  } else if (v.ad_status === 3) {
                    AdIcon = FaPause;
                    ad_color = "paused";
                    ad_label = `Paused`;
                  } else if (v.ad_status === 4) {
                    AdIcon = FaPause;
                    ad_color = "paused";
                    ad_label = "Archived";
                  } else {
                    return null;
                  }
                  return (
                    <Link
                      key={v.id}
                      to={`/user/advertising/dashboard/ads/${v.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={css({
                        maxWidth: "230px",
                        backgroundColor: ad_color === "active" ? "trellisPalette.12/30" : "trellisPalette.16/30",
                        borderRadius: "full",
                        display: "inline-flex",
                        alignItems: "center",
                        height: "24px",
                        justifyContent: "start",
                        fontSize: "xsmall",
                        paddingY: "xsmall",
                        paddingX: "small",
                        gap: "xsmall",
                        textDecoration: "none",
                        _hover: {
                          backgroundColor: ad_color === "active" ? "trellisPalette.12/50" : "trellisPalette.16/50",
                        },
                      })}
                    >
                      <AdIcon />
                      <span
                        className={css({
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        })}
                      >{`${v.name} (${ad_label})`}</span>
                    </Link>
                  );
                })}
                {unmanagedAds?.length ?
                  <span className="d-block p-2">
                    {managedAds?.length ?
                      `+${unmanagedAds?.length} media plan${unmanagedAds?.length === 1 ? "" : "s"} not managed by Trellis`
                    : <div
                        className={css({
                          maxWidth: "230px",
                          borderRadius: "full",
                          display: "inline-flex",
                          alignItems: "center",
                          height: "24px",
                          justifyContent: "start",
                          fontSize: "xsmall",
                          paddingY: "xsmall",
                          paddingX: "small",
                          boxSizing: "border-box",
                          gap: "xsmall",
                          textDecoration: "none",
                        })}
                      >{`${unmanagedAds?.length} media plan${unmanagedAds?.length === 1 ? "" : "s"} not managed by Trellis`}</div>
                    }
                  </span>
                : null}
              </Wrap>
            </VStack>
          );
        },
      },
    ];

    if (!currentCategory) {
      columns.push({
        accessorKey: "category",
        cell: ({ getValue }) => {
          const productCategoryName = categories?.find(
            (category) => category.id === parseInt(getValue()),
          )?.leaf_category_name;

          return (
            <div className="text-center">
              <Link
                to={
                  orgIsWalmartVendor ?
                    `/user/advertising/dashboard/category/${getValue()}`
                  : `/user/merchandising/category/${getValue()}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                {productCategoryName}
              </Link>
            </div>
          );
        },
      });
    }
    return columns;
  }, [currentCategory, categories, orgIsWalmartVendor]);

  const categoryName = currentCategory ? currentCategoryDetails.name : "All Products";

  const table = useReactTable({
    columns,
    data: productSearchQuery.data?.data?.results || DEFAULT_ARRAY,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    manualPagination: true,
    state: {
      rowSelection: selection,
    },
    onRowSelectionChange: setSelection,
    getRowId: (row) => row.id,
    meta: {
      showSkeleton: productSearchQuery.isFetching && (!productSearchQuery.data || productSearchQuery.isPlaceholderData),
    },
  });

  const hasPages = productSearchQuery.data?.data?.next || productSearchQuery.data?.data?.previous;

  if (actionMutation.isPending) {
    return <Loading />;
  }

  return (
    <>
      <Card>
        <VStack alignItems="stretch" ref={topOfListingElement}>
          <Wrap>
            <p className={css({ margin: 0 })}>Products - {categoryName}</p>
            {currentCategory ?
              <Link
                to={`/user/merchandising/category/${currentCategory}`}
                className={cx(buttonRecipe({ variant: "link" }), css({ fontSize: "small" }))}
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit Category
              </Link>
            : null}
          </Wrap>

          <SearchInput
            label={"Search Products"}
            value={searchQuery}
            placeholder={"Query..."}
            onChange={setSearchQuery}
          />

          <Accordion title="Advanced">
            <VStack alignItems="stretch" gap="xsmall">
              <Wrap gap="xlarge" justify="space-between">
                <RadioGroup
                  className={css({
                    maxWidth: "50%",
                  })}
                  orientation={"horizontal"}
                  label="Match On"
                  items={[
                    {
                      value: "icontains",
                      label: "Contains",
                    },
                    {
                      value: "istartswith",
                      label: "Starts With",
                    },
                    {
                      value: "iendswith",
                      label: "Ends With",
                    },
                    {
                      value: "iregex",
                      label: "Regex",
                    },
                  ]}
                  value={matchOn}
                  onChange={setMatchOn}
                />
                <div>
                  <label
                    className={css({
                      display: "flex",
                      gap: "xsmall",
                      color: "text.label",
                      fontSize: "medium",
                      fontWeight: "bold",
                      flexWrap: "nowrap",
                    })}
                  >
                    Fields
                  </label>
                  <HStack justify="space-around" maxWidth="50%">
                    <Checkbox
                      value={searchFields.product_title}
                      onChange={(value) =>
                        setSearchFields((old) => ({
                          ...old,
                          product_title: value,
                        }))
                      }
                      label="Title"
                    />
                    <Checkbox
                      value={searchFields.sku}
                      onChange={(value) =>
                        setSearchFields((old) => ({
                          ...old,
                          sku: value,
                        }))
                      }
                      label="SKU"
                    />
                    <Checkbox
                      value={searchFields.asin}
                      onChange={(value) =>
                        setSearchFields((old) => ({
                          ...old,
                          asin: value,
                        }))
                      }
                      label={channel.currentChannel === "walmart" ? "Item ID" : "ASIN"}
                    />
                    {channel.currentChannel === "amazon" && (
                      <Checkbox
                        value={searchFields.parent_asin}
                        onChange={(value) =>
                          setSearchFields((old) => ({
                            ...old,
                            parent_asin: value,
                          }))
                        }
                        label={"Parent ASIN"}
                      />
                    )}
                  </HStack>
                </div>
              </Wrap>
              <TextAreaField
                label={channel.currentChannel === "walmart" ? "Search Item IDs" : "Search ASINs"}
                placeholder={
                  channel.currentChannel === "walmart" ? "Copy and paste Item IDs" : "Copy and paste ASINs or SKUs"
                }
                value={searchAsins}
                onChange={setSearchAsins}
              />
              <Button alignSelf="end" variant="secondary" onPress={() => setSearchAsins("")}>
                Clear
              </Button>
            </VStack>
          </Accordion>
        </VStack>
      </Card>
      <HStack justify="space-between">
        <Button
          isDisabled={selectedIds.length === 0}
          onPress={() => {
            setIsOpenMoveToDialog(true);
          }}
        >
          <LuMoveUpRight /> Move to
        </Button>
        <MoveToDialog
          isOpen={isOpenMoveToDialog}
          onClose={() => setIsOpenMoveToDialog(false)}
          actionMutation={actionMutation}
          allSelected={allSelected}
          selection={selectedIds}
        />
        <ButtonGroup
          buttons={[
            {
              value: "all",
              label: "All Products",
            },
            {
              value: "true",
              label: "Has Ad",
            },
            {
              value: "false",
              label: "Has No Ad",
            },
          ]}
          value={searchFields.has_an_ad}
          onSelect={(newValue) =>
            setSearchFields({
              ...searchFields,
              has_an_ad: newValue,
            })
          }
        />
      </HStack>
      <Card>
        {hasPages && (table.getIsAllPageRowsSelected() || allSelected) ?
          <ExplainerBox>
            {allSelected ?
              <>
                All products matching this query selected.{" "}
                <Button className={css({ display: "contents" })} variant="link" onPress={() => setAllSelected(false)}>
                  Clear selection
                </Button>
                .
              </>
            : <>
                All {selectedIds.length > 25 ? 25 : selectedIds.length} products on this page are selected. Select{" "}
                <Button className={css({ display: "contents" })} variant="link" onPress={() => setAllSelected(true)}>
                  all products
                </Button>{" "}
                that matched your query instead?
              </>
            }
          </ExplainerBox>
        : null}
        <Table table={table} />
        {hasPages ?
          <HStack justify="end">
            <Button
              variant={"secondary"}
              onPress={handlePrevious}
              isDisabled={!productSearchQuery.data?.data?.previous || productSearchQuery.isPlaceholderData}
            >
              Previous
            </Button>
            <Button
              variant={"secondary"}
              onPress={handleNext}
              isDisabled={!productSearchQuery.data?.data?.next || productSearchQuery.isPlaceholderData}
            >
              Next
            </Button>
          </HStack>
        : null}
      </Card>
    </>
  );
};

export default ProductListing;
